import React, {
  useEffect,
  useContext,
  useState,
  useCallback,
  useRef,
} from "react"
import * as z from "zod"
import Cookies from "js-cookie"
import Loader from "../../ui/loader"
import Recaptcha from "../register-form/Recaptcha"
import useLoading from "../../../hooks/useLoading"
import secureLocalStorage from "react-secure-storage"
import { Button } from "../../ui/button"
import { CommonFunctionCtx } from "../../../context/commonFunctionContext"
import { ContactRequests } from "../../../api/app.service"
import { Dialog, DialogContent, DialogHeader } from "../../ui/dialog"
import { getUserStats } from "../../../utils/getUserStats"
import { Input } from "../../ui/input"
import { InputOTPGroup, InputOTPSlot, InputOTP } from "../../ui/input-otp"
import { isCoachInCommissionTracking } from "../../../utils/commissionTracking"
import { Label } from "../../ui/label"
import { LoginFormContext } from "../login-form/context"
import {
  SvgCheckbox,
  SvgCheckboxOff,
  SvgClose,
  SvgZoeeLogoGradiant,
} from "../../icons"
import { useAuthModal } from "../use-auth-modal"
import { useNavigate } from "react-router-dom"
import { UserCtx } from "../../../context/userContext"
import { UserExists, EmailLogin } from "../../../api/auth.service"
import { userflowInitialize } from "../../../utils/userflow-integration"
import { useToast } from "../../../hooks/useToast"
import { VerifyConfirmationCode } from "../../../api/auth.service"

interface Props {
  onSendOtp: (email: string) => void
}

const doesEmailExist = async (email: string) => {
  try {
    const data = await UserExists.verifyUserExists({
      attributes_to_verify: { email },
    })
    return data
  } catch (error) {
    console.error(error)
    return false
  }
}

const validateEmail = (email: string) => z.string().email().safeParse(email)

const validatePassword = (password: string) =>
  z
    .string()
    .min(8, "Invalid password")
    .regex(/^(?=.*[0-9])(?=.*\W).{8,}$/, {
      message: "Login failed. Please check your credentials and try again.",
    })
    .safeParse(password)

const GeneralSignInForm: React.FC<Props> = ({ onSendOtp }) => {
  type AuthMode = "password" | "otp_request" | "otp_verify"

  const {
    email,
    setEmail,
    password,
    setPassword,
    getSetupInfo,
    updateTimezone,
    onSuccess,
  } = useContext(LoginFormContext)
  const { getUser } = useContext(CommonFunctionCtx)
  const { showRegister } = useAuthModal()
  const { user } = useContext(UserCtx)
  const { toast } = useToast()
  const { startLoading, stopLoading } = useLoading()
  const [loading, setLoading] = useState(false)
  const [emailError, setEmailError] = useState<string | undefined>()
  const [rememberMe, setRememberMe] = useState(false)
  const [authMode, setAuthMode] = useState<AuthMode>("password")
  const [otpCode, setOtpCode] = useState("")
  const [otpSession, setOtpSession] = useState<any>(null)
  const [sendingOtp, setSendingOtp] = useState(false)
  const [showresetModal, setShowresetModal] = useState(false)
  const [isNotRobot, setIsNotRobot] = useState<boolean>(false)
  const [userIsTemp, setUserIsTemp] = useState(false)
  const debounceTimer = useRef<NodeJS.Timeout | null>(null)
  const navigate = useNavigate()
  const coachRedirect = localStorage.getItem("coachRedirect") || "/coach"
  const memberRedirect = localStorage.getItem("memberRedirect") || "/member"
  const coachDetails = (() => {
    const raw = secureLocalStorage.getItem("coachDetails")
    return typeof raw === "string" ? JSON.parse(raw) : null
  })()
  const GENERIC_LOGIN_ERROR =
    "Login failed. Please check your credentials and try again."

  const redirectURL = () => {
    if (user?.activeProfile === "coach") {
      navigate(coachRedirect)
    } else {
      navigate(memberRedirect)
    }
  }

  const canSubmit =
    !!email &&
    !!isNotRobot &&
    (authMode === "password" ? !!password : otpCode.length === 6)

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setEmail(value)
    setEmailError(undefined)
  }

  useEffect(() => {
    const rememberedEmail = Cookies.get("rememberedEmail")
    if (rememberedEmail) {
      setEmail(rememberedEmail)
      setRememberMe(true)
    }
  }, [setEmail])

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setLoading(true)

    try {
      const emailCheck = validateEmail(email)
      if (!emailCheck.success) {
        toast.error(GENERIC_LOGIN_ERROR)
        return
      }

      if (authMode === "password") {
        const passwordCheck = validatePassword(password)
        if (!passwordCheck.success) {
          toast.error(GENERIC_LOGIN_ERROR)
          return
        }
      }

      rememberMe
        ? Cookies.set("rememberedEmail", email, { expires: 30 })
        : Cookies.remove("rememberedEmail")

      const success = await login()
      const isTemp = localStorage.getItem("isTemp")

      if (success) {
        if (isTemp === "false" || !isTemp) {
          redirectURL()
        } else {
          navigate("/reset-password")
        }
      }
    } catch (err) {
      console.error(err)
      toast.error(GENERIC_LOGIN_ERROR)
    } finally {
      setLoading(false)
    }
  }

  const login = async () => {
    try {
      let data: any = null

      if (authMode === "password") {
        data = await EmailLogin.loginEmail({
          email,
          password,
          stats: getUserStats(),
        })
      }

      if (authMode === "otp_verify") {
        if (otpCode.length !== 6) {
          toast.error("Enter the 6-digit code.")
          return false
        }

        if (!otpSession) {
          toast.error("Code expired. Please resend.")
          return false
        }

        data = await VerifyConfirmationCode.verifyConfirmationCode({
          confirmation_code: otpCode,
          session: otpSession.session,
          user_id: otpSession.user_id,
          stats: getUserStats(),
        })
      }

      if (!data) throw new Error("No auth data")

      secureLocalStorage.setItem("user", JSON.stringify(data))

      await Promise.all([
        getSetupInfo(data.user_id),
        updateTimezone(data.user_id, data.access_token),
      ])

      const authedUser = await getUser()
      userflowInitialize(authedUser.user_id)
      onSuccess(authedUser)
      if (coachDetails) {
        try {
          await ContactRequests.createContactConnection({
            sender_profile_id: coachDetails.coach_profile_id,
            receiver_profile_id: authedUser.activeProfileId,
            user_environment: process.env.REACT_APP_USER_ENVIRONMENT || "",
            status: authedUser.isCoach
              ? "c2c"
              : isCoachInCommissionTracking(coachDetails.endpoint_slug)
              ? "zoee lead"
              : "connected",
          })
        } catch (err) {
          console.error("Failed to create contact connection", err)
        } finally {
          secureLocalStorage.removeItem("coachDetails")
        }
      }
      return true
    } catch (err) {
      console.error(err)
      toast.error(GENERIC_LOGIN_ERROR)
      return false
    }
  }

  const forgotPassword = async () => {
    startLoading("Sending instructions")
    const pathname = window.location.pathname

    setEmailError(undefined)

    if (!email) {
      setEmailError("Please enter your email.")
      stopLoading()
      return
    }

    try {
      const emailExists = await doesEmailExist(email)
      if (!emailExists) {
        setEmailError("Email not found.")
        return
      }

      const resetPassword = await EmailLogin.resetPasswordSendLink({
        email,
        pathname,
      })

      if (resetPassword.message === "complete") {
        setShowresetModal(true)
      }
    } catch (error) {
      console.error(error)
      toast.error("Something went wrong. Please try again.")
    } finally {
      stopLoading()
    }
  }

  const handleSendOtp = async (emailToSend: string) => {
    setLoading(true)
    try {
      setSendingOtp(true)

      const session = await EmailLogin.loginOTP({ email: emailToSend })
      setOtpSession(session)
      setAuthMode("otp_verify")

      toast.success("We sent a one-time code to your email.")
    } catch (err) {
      console.error(err)
      toast.error(GENERIC_LOGIN_ERROR)
    } finally {
      setLoading(false)
      setSendingOtp(false)
    }
  }

  const determineAuthMode = useCallback(async (email: string) => {
    try {
      const data = await UserExists.verifyUserExists({
        attributes_to_verify: { email },
      })

      if (!data?.user_found) return

      if ([1, 2].includes(data.is_temp)) {
        localStorage.setItem("isTemp", "true")
        setAuthMode("otp_request")
        setUserIsTemp(true)
      } else {
        localStorage.setItem("isTemp", "false")
        setAuthMode("password")
      }
    } catch (err) {
      console.error("Failed to determine auth mode", err)
    }
  }, [])

  useEffect(() => {
    if (!email) return

    const emailValid = z.string().email().safeParse(email)
    if (!emailValid.success) return

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current)
    }

    debounceTimer.current = setTimeout(async () => {
      await determineAuthMode(email)
    }, 500)

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current)
      }
    }
  }, [email, determineAuthMode])

  return (
    <div className="bg-white rounded-xl flex flex-col items-center justify-center p-8">
      <div className="mt-4">
        <SvgZoeeLogoGradiant className="h-[50px] w-auto block mx-auto" />
      </div>
      <div className="text-center mt-8 mb-4">
        <span className="text-lg font-bold block">
          Enter your login details
        </span>
      </div>
      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-4 mt-4 w-[65%]"
      >
        <Input
          label="Email"
          placeholder="Enter your email"
          value={email}
          onChange={handleEmailChange}
          error={emailError}
          disabled={loading}
          autoFocus
        />
        {authMode === "password" && (
          <>
            <Input
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
            />
          </>
        )}
        {authMode === "otp_verify" && (
          <div className="flex flex-col items-center w-full mt-2">
            <Label className="text-center mb-3">
              Enter the 6-digit code sent to your email
            </Label>

            <InputOTP
              maxLength={6}
              value={otpCode}
              onChange={setOtpCode}
              disabled={loading}
              autoFocus
            >
              <InputOTPGroup className="justify-center">
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <InputOTPSlot key={i} index={i} />
                ))}
              </InputOTPGroup>
            </InputOTP>
          </div>
        )}
        <label className="-mb-4 md:mb-0 flex gap-3 text-muted-foreground items-center text-sm">
          {rememberMe ? (
            <SvgCheckbox />
          ) : (
            <SvgCheckboxOff
              fill="#d1d5db"
              className="border border-[gray] rounded-md"
            />
          )}
          <input
            type="checkbox"
            checked={rememberMe}
            className="hidden"
            onChange={(e) => setRememberMe(e.target.checked)}
          />
          Remember me
        </label>
        <span className="items-center justify-center flex -mb-6">
          <Recaptcha setIsNotRobot={setIsNotRobot} />
        </span>
        <Button
          className="mt-4 w-full font-bold md:mb-4"
          disabled={!email || !isNotRobot || loading}
          variant="rounded"
          type="button"
          onClick={async () => {
            if (authMode === "otp_request") {
              await handleSendOtp(email)
            } else {
              document.querySelector("form")?.requestSubmit()
            }
          }}
        >
          {loading ? (
            <Loader />
          ) : authMode === "otp_request" ? (
            "Send one-time password"
          ) : (
            "Access my portal"
          )}
        </Button>
      </form>
      {authMode === "password" && (
        <div className="flex flex-col gap-1 text-sm items-center">
          <Button
            variant="link"
            type="button"
            onClick={() => handleSendOtp(email)}
            disabled={sendingOtp}
          >
            Send one-time password
          </Button>

          <Button variant="link" type="button" onClick={forgotPassword}>
            Forgot Password?
          </Button>
        </div>
      )}
      <div className="flex flex-col gap-1 mt-3 text-sm items-center">
        {authMode === "otp_verify" && (
          <Button
            variant="link"
            type="button"
            onClick={() => handleSendOtp(email)}
            disabled={sendingOtp}
          >
            Resend one-time password
          </Button>
        )}

        {authMode === "otp_verify" && (
          <Button
            variant="link"
            type="button"
            onClick={() => {
              setOtpCode("")
              setAuthMode("password")
              localStorage.removeItem("isTemp")
            }}
          >
            Back to password
          </Button>
        )}
      </div>
      {!userIsTemp && (
        <div className="flex items-center justify-center text-center w-[100%] whitespace-nowrap ">
          <p className="text-[#63696F] text-sm font-semibold ml-2">
            Don't have an account?
          </p>
          <Button
            variant="link"
            type="button"
            onClick={() => showRegister({ accountType: "coach" })}
            className="-mr-2 -ml-[10px]"
          >
            Sign Up
          </Button>
        </div>
      )}
      <Dialog open={showresetModal} onOpenChange={setShowresetModal}>
        <DialogContent className="">
          <DialogHeader className="text-center items-center">
            <button
              onClick={() => setShowresetModal(false)}
              className="absolute top-6 right-6 text-gray-500 hover:text-gray-800"
              aria-label="Close"
            >
              <SvgClose />
            </button>
            <p className="-mt-4">
              Instructions to reset your password have been sent to your email.
              Please check your inbox and follow the steps provided.
              <br />
              <br />
              Have questions? Contact zoee support at
              <br />
              <a
                className="underline font-bold"
                href="mailto:businessblueprint@integrativenutrition.com"
                target="_blank"
                rel="noreferrer"
              >
                support@zoee.com
              </a>
            </p>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default GeneralSignInForm
